/**
 * useReadiness Hook
 *
 * Custom hook for polling service readiness status during cold starts.
 * Used by the BootScreen component to display service status and auto-redirect.
 *
 * Phase 58: Scale-to-Zero with Boot Screen
 */

import { useState, useEffect, useCallback, useRef } from 'react'

export type ServiceStatusType = 'ready' | 'starting' | 'error' | 'disabled'

export interface ServiceStatus {
  status: ServiceStatusType
  latency_ms?: number
  message?: string
  error?: string
}

export interface ReadinessResponse {
  ready: boolean
  estimated_seconds: number
  timestamp: string
  services: Record<string, ServiceStatus>
}

interface UseReadinessOptions {
  requiredServices: string[]
  pollInterval?: number // Default: 2000ms
  maxWait?: number // Default: 120000ms (2 min)
  enabled?: boolean // Default: true
}

interface UseReadinessResult {
  ready: boolean
  services: Record<string, ServiceStatus>
  estimatedSeconds: number
  error: Error | null
  isPolling: boolean
  isTimedOut: boolean
}

const DEFAULT_POLL_INTERVAL = 2000
const DEFAULT_MAX_WAIT = 120000

/**
 * Poll /api/readiness endpoint and track service status
 */
export function useReadiness(options: UseReadinessOptions): UseReadinessResult {
  const {
    requiredServices,
    pollInterval = DEFAULT_POLL_INTERVAL,
    maxWait = DEFAULT_MAX_WAIT,
    enabled = true,
  } = options

  const [ready, setReady] = useState(false)
  const [services, setServices] = useState<Record<string, ServiceStatus>>({})
  const [estimatedSeconds, setEstimatedSeconds] = useState(0)
  const [error, setError] = useState<Error | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [isTimedOut, setIsTimedOut] = useState(false)

  const startTimeRef = useRef<number>(0)
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const maxWaitTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const cleanup = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
    if (maxWaitTimeoutRef.current) {
      clearTimeout(maxWaitTimeoutRef.current)
      maxWaitTimeoutRef.current = null
    }
    setIsPolling(false)
  }, [])

  const fetchReadiness = useCallback(async (): Promise<ReadinessResponse | null> => {
    try {
      const params = requiredServices.length > 0
        ? `?required=${requiredServices.join(',')}`
        : ''
      const response = await fetch(`/api/readiness${params}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`Readiness check failed: ${response.status}`)
      }

      return await response.json()
    } catch (err) {
      console.error('Readiness fetch error:', err)
      setError(err instanceof Error ? err : new Error(String(err)))
      return null
    }
  }, [requiredServices])

  const poll = useCallback(async () => {
    const data = await fetchReadiness()

    if (!data) {
      // Error occurred, schedule retry
      pollTimeoutRef.current = setTimeout(poll, pollInterval)
      return
    }

    setServices(data.services)
    setEstimatedSeconds(data.estimated_seconds)
    setError(null)

    if (data.ready) {
      setReady(true)
      cleanup()
      console.log('Services ready, stopping poll')
      return
    }

    // Not ready yet, schedule next poll
    pollTimeoutRef.current = setTimeout(poll, pollInterval)
  }, [fetchReadiness, pollInterval, cleanup])

  // Start polling when enabled
  useEffect(() => {
    if (!enabled) {
      cleanup()
      return
    }

    // If no required services, mark as ready immediately
    if (requiredServices.length === 0) {
      setReady(true)
      setIsPolling(false)
      return
    }

    // Reset state
    setReady(false)
    setIsTimedOut(false)
    setError(null)
    setIsPolling(true)
    startTimeRef.current = Date.now()

    // Start polling
    poll()

    // Set max wait timeout
    maxWaitTimeoutRef.current = setTimeout(() => {
      console.log('Readiness check timed out')
      setIsTimedOut(true)
      cleanup()
    }, maxWait)

    // Cleanup on unmount or when deps change
    return cleanup
  }, [enabled, requiredServices.join(','), maxWait, poll, cleanup])

  return {
    ready,
    services,
    estimatedSeconds,
    error,
    isPolling,
    isTimedOut,
  }
}

export default useReadiness
